<script setup lang="ts">
import { MContainerRow, MContainerColumn, NotificationModel, MContainer, NotificationEvents, WeblinkFileUploadEmitModel } from "@xlabmedical/medic-web-framework";
import { MNotificationTab } from "@xlabmedical/medic-web-engine";
import { NotificationChannelType } from "@xlabmedical/medic-api";
import { SelectButtonChangeEvent } from "primevue";
import { ProjectModel } from "@xlabmedical/implant-planner";
import { useI18n } from "vue-i18n";
import { ChatMessageModel } from "@/models/ChatMessageModel";

const props = withDefaults(
    defineProps<{
        hasLegacyProject?: boolean;
        selectedPatientName?: string;
    }>(),
    {
        hasLegacyProject: false,
        selectedPatientName: "",
    },
);

const projectNotifications = defineModel<NotificationModel[]>("projectNotifications", { default: [] });
const orderNotifications = defineModel<NotificationModel[]>("orderNotifications", { default: [] });
const selectedOrderId = defineModel<string | undefined>("selectedOrderId");
type OrderNotificationsEntry = {
    orderNotifications?: NotificationModel[];
    projectId?: number;
    caseNumber?: string;
    weblinkProjectName?: string;
    unreadNotificationsPerChannel?: number | Record<string, number | null | undefined>;
};

type NotificationOption = {
    name: string;
    index: number;
    projectName?: string;
    projectId: number;
    unreadNotificationCount: number;
    value: string;
    caseNumber?: string;
    orderNotifications?: NotificationModel[];
};

const ordersNotifications = defineModel<OrderNotificationsEntry[]>("ordersNotifications", { default: [] });

const { t } = useI18n();

const mainProject = defineModel<ProjectModel>("mainProject");
const isUserWeblinkOperator = defineModel<boolean>("isUserWeblinkOperator", { default: false });
const loading = defineModel<boolean>("loading", { default: true });
const uploadingAttachment = defineModel<boolean>("uploadingAttachment", { default: false });

type NotificationUploadPayload = {
    projectName?: string;
    upload: WeblinkFileUploadEmitModel;
};

const emits = defineEmits<{
    (e: "sendMessage", value: ChatMessageModel): void;
    (e: "updateSeenNotifications", value: number): void;
    (e: "uploadFileAndShareToChat", value: NotificationUploadPayload): void;
    (e: "openSTL", value: string): void;
}>();

const selectedChatValue = ref<string>("chat");
const lastSelectedChatValue = ref<string>("chat");
const options = ref<NotificationOption[]>([
    {
        name: t("weblink.chatnotifications"),
        index: 0,
        projectName: mainProject.value?.patientName ?? props.selectedPatientName,
        projectId: 111,
        unreadNotificationCount: 0,
        value: "chat",
    },
]);

const currentNotifications = ref<NotificationModel[]>([]);

const projectName = ref<string | undefined>(undefined);
const selectedChat = computed(() => options.value.find((option) => option.value === selectedChatValue.value));
const displayOrderNotifications = ref<NotificationModel[]>([]);
const orderNotificationsKey = computed(() => `${selectedChatValue.value}:${displayOrderNotifications.value.length}`);

const toUnreadNotificationCount = (unread: number | Record<string, number | null | undefined> | null | undefined) => {
    if (typeof unread === "number") return unread;
    if (unread && typeof unread === "object") {
        return Object.values(unread).reduce<number>((sum, value) => sum + (typeof value === "number" ? value : 0), 0);
    }
    return 0;
};

const applySelectionByValue = (value: string) => {
    const target = options.value.find((option) => option.value === value);
    if (!target) return;
    selectedChatValue.value = target.value;
    projectName.value = target.projectName;
    displayOrderNotifications.value = [...(target.orderNotifications ?? [])];
};

const selectOrderById = (orderId?: string) => {
    if (!orderId) return;
    const target = options.value.find((option) => option.caseNumber === orderId);
    if (!target) return;
    applySelectionByValue(target.value);

    if (target.unreadNotificationCount > 0) {
        markAsRead(target.projectId);
        emits("updateSeenNotifications", target.projectId);
    }
};

const onChatSelectBtnClick = (event: SelectButtonChangeEvent) => {
    const target = options.value.find((option) => option.value === event?.value);
    if (!target) {
        selectedChatValue.value = lastSelectedChatValue.value;
        applySelectionByValue(lastSelectedChatValue.value);
        return;
    }

    lastSelectedChatValue.value = target.value;
    applySelectionByValue(target.value);

    if (target.unreadNotificationCount && target.unreadNotificationCount > 0) {
        markAsRead(target.projectId);
        emits("updateSeenNotifications", target.projectId);
    }
};

const sendMessage = (chatInput: string) => {
    const msg = {
        text: chatInput,
        projectName: projectName.value,
    } as ChatMessageModel;

    emits("sendMessage", msg);
};

const notificationCallbacks = {
    async sendMessage(text: string) {
        sendMessage(text);
    },
    async addNote(text: string) {
        sendMessage(text);
    },
} as NotificationEvents;

const uploadFileAndShareToChat = (upload: WeblinkFileUploadEmitModel) => {
    emits("uploadFileAndShareToChat", {
        projectName: projectName.value,
        upload,
    });
};

const hasOrders = computed(() => options.value.length > 1);

function markAsRead(pid?: number) {
    if (!pid && pid !== 0) return;
    const i = options.value.findIndex((o) => o.projectId === pid);
    if (i !== -1) {
        // Keep reactivity by replacing the object
        options.value[i] = { ...options.value[i], unreadNotificationCount: 0 };
    }
}

const hasProjects = computed(() => {
    return Object.keys(mainProject.value || {}).length > 0 || props.hasLegacyProject;
});

onMounted(async () => {
    loading.value = false;
});

const rebuildOptions = () => {
    const baseOptions: NotificationOption[] = [
        {
            name: t("weblink.chatnotifications"),
            index: 0,
            projectName: mainProject.value?.patientName ?? props.selectedPatientName,
            projectId: 111,
            unreadNotificationCount: 0,
            value: "chat",
        },
    ];

    const orderOptions = ordersNotifications.value.map((entry, i) => ({
        name: `${t("weblink.orderID")} : ${entry.caseNumber ?? "-"}`,
        index: i + 1,
        projectName: entry.weblinkProjectName,
        projectId: entry.projectId ?? 0,
        unreadNotificationCount: toUnreadNotificationCount(entry.unreadNotificationsPerChannel),
        caseNumber: entry.caseNumber,
        value: entry.caseNumber ?? String(entry.projectId ?? i + 1),
        orderNotifications: entry.orderNotifications ?? [],
    }));

    options.value = [...baseOptions, ...orderOptions];
    syncSelectedChat();
    if (!options.value.some((option) => option.value === selectedChatValue.value)) {
        selectedChatValue.value = options.value[0]?.value ?? "chat";
    }
};

const syncSelectedChat = () => {
    if (selectedOrderId.value) {
        const targetByOrderId = options.value.find((option) => option.caseNumber === selectedOrderId.value);
        if (targetByOrderId) {
            applySelectionByValue(targetByOrderId.value);
            return;
        }
    }
    if (selectedChat.value?.projectId != null) {
        const targetByProjectId = options.value.find((option) => option.projectId === selectedChat.value?.projectId);
        if (targetByProjectId) {
            applySelectionByValue(targetByProjectId.value);
            return;
        }
    }
    selectedChatValue.value = "chat";
    applySelectionByValue("chat");
};

watch(
    () => selectedOrderId.value,
    (orderId) => {
        selectOrderById(orderId);
    },
);

watch(
    () => ordersNotifications.value,
    () => {
        rebuildOptions();
        if (selectedOrderId.value) {
            selectOrderById(selectedOrderId.value);
        }
    },
    { deep: true, immediate: true },
);
</script>
<template>
    <MContainerColumn v-if="!loading">
        <MContainerRow v-if="hasOrders" class="mb-4">
            <div class="chat-select-scroll">
                <SelectButton
                    v-model="selectedChatValue"
                    class="chat-select-button"
                    :options="options"
                    dataKey="name"
                    optionLabel="name"
                    optionValue="value"
                    @change="onChatSelectBtnClick">
                    <template #option="slotProps">
                        <div class="flex items-center gap-2">
                            <span>{{ slotProps.option.name }}</span>
                            <span
                                v-if="slotProps.option.unreadNotificationCount > 0"
                                class="ml-2 text-white text-xs font-bold px-2 py-0.5 rounded-full"
                                style="background: var(--primary-color)">
                                {{ slotProps.option.unreadNotificationCount }}
                            </span>
                        </div>
                    </template>
                </SelectButton>
            </div>
        </MContainerRow>
        <MContainerRow v-if="projectNotifications && selectedChat && selectedChat.index === 0" class="h-full overflow-y-auto overflow-x-auto">
            <MNotificationTab
                :channel="NotificationChannelType.CHAT"
                :isUserWeblinkOperator="isUserWeblinkOperator"
                :notifications="projectNotifications"
                :fromPlanner="true"
                :callbacks="notificationCallbacks"
                :showAddNote="false"
                :showShareToChatOption="false"
                v-model:uploadingAttachment="uploadingAttachment"
                class="grow w-full"
                :disableSendMessage="!hasProjects"
                @openSTL="emits('openSTL', $event)"
                @uploadFileAndShareToChat="uploadFileAndShareToChat" />
        </MContainerRow>
        <MContainerRow v-if="currentNotifications && selectedChat && selectedChat.index > 0" class="h-full overflow-y-auto overflow-x-auto">
            <MNotificationTab
                :channel="NotificationChannelType.CHAT"
                :isUserWeblinkOperator="isUserWeblinkOperator"
                :notifications="displayOrderNotifications"
                :fromPlanner="true"
                :callbacks="notificationCallbacks"
                :showAddNote="false"
                v-model:uploadingAttachment="uploadingAttachment"
                :key="`order-${orderNotificationsKey}`"
                class="grow w-full"
                @openSTL="emits('openSTL', $event)"
                @uploadFileAndShareToChat="uploadFileAndShareToChat" />
        </MContainerRow>
    </MContainerColumn>
    <MContainerColumn v-else>
        <MContainer class="items-center justify-center h-full">
            <ProgressSpinner animationDuration="0.6s" />
        </MContainer>
    </MContainerColumn>
</template>
<style scoped>
.chat-select-scroll {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
}

:deep(.chat-select-button.p-selectbutton) {
    display: inline-flex;
    flex-wrap: nowrap;
    min-width: max-content;
    width: max-content;
}

:deep(.chat-select-button .p-togglebutton) {
    flex: 0 0 auto;
    white-space: nowrap;
}
</style>
